/**
 * Recipe Builder — Product drawer tab for ingredients, costing, and prep steps.
 * Admin sees full costing; Manager sees recipe + instructions (NO prices).
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Plus, Trash2, Search, Check, Loader2, ChefHat, DollarSign, ClipboardList,
  AlertTriangle, Download, X, Scale, Timer, ShieldAlert, Eye,
} from 'lucide-react';
import { KitchenCardPreview } from './KitchenCardPreview';
import { apiFetch } from '../../../data/ApiModuleRepository';
import type { Ingredient, Recipe, RecipeIngredient, Allergen, PurchaseUnit } from '../types';
import { ALLERGEN_LIST, PURCHASE_UNITS } from '../types';
import { MENU_DATA_UPDATED_EVENT } from '../utils';
import {
  recalcRecipe, createRecipeIngredient, createBlankRecipe,
  foodCostPct, profitMargin,
} from '../services/RecipeEngineService';
import { cn } from '../../../ui/cn';
import { useToast } from '../../../ui/Toast';

interface RecipeTabProps {
  profileId: string;
  itemId: string;
  itemName: string;
  itemImage?: string;
  basePrice: number;
  orgId: string;
  canViewCost: boolean;
  canEdit: boolean;
  onCostUpdate?: (totalCost: number) => void;
  brandName?: string;
  brandLogo?: string;
  currencySymbol?: string;
}

export function RecipeTab({
  profileId, itemId, itemName, itemImage, basePrice, orgId, canViewCost, canEdit, onCostUpdate,
  brandName, brandLogo, currencySymbol = 'QAR',
}: RecipeTabProps) {
  const toast = useToast();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [pantry, setPantry] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showIngSearch, setShowIngSearch] = useState(false);
  const [showCardPreview, setShowCardPreview] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Build pantry map for quick lookup
  const pantryMap = useMemo(() => {
    const m = new Map<string, Ingredient>();
    pantry.forEach(i => m.set(i.id, i));
    return m;
  }, [pantry]);

  // ── Load recipe + pantry ──
  useEffect(() => {
    (async () => {
      try {
        const [recipeRes, pantryRes] = await Promise.all([
          apiFetch<{ data: Recipe | null }>(`/api/menu/recipes/${profileId}/${itemId}`),
          apiFetch<{ data: Ingredient[] }>('/api/menu/ingredients'),
        ]);
        setPantry(pantryRes.data || []);
        if (recipeRes.data) {
          // Recalc with latest pantry prices
          const pm = new Map<string, Ingredient>();
          (pantryRes.data || []).forEach(i => pm.set(i.id, i));
          const { ingredients, totalCost } = recalcRecipe(recipeRes.data.ingredients, pm);
          setRecipe({ ...recipeRes.data, ingredients, totalCost });
        } else {
          setRecipe(createBlankRecipe(crypto.randomUUID(), itemId, profileId, orgId));
        }
      } catch {
        toast.error('Load Error', 'Could not load recipe data.');
        setRecipe(createBlankRecipe(crypto.randomUUID(), itemId, profileId, orgId));
      }
      setLoading(false);
    })();
  }, [profileId, itemId]);

  // ── Ingredient search results ──
  const searchResults = useMemo(() => {
    if (!searchTerm) return pantry.slice(0, 20);
    const q = searchTerm.toLowerCase();
    return pantry.filter(i => i.name.toLowerCase().includes(q)).slice(0, 20);
  }, [pantry, searchTerm]);

  // ── Mutators ──
  const updateRecipe = useCallback((fn: (r: Recipe) => Recipe) => {
    setRecipe(prev => {
      if (!prev) return prev;
      const updated = fn(prev);
      setDirty(true);
      return updated;
    });
  }, []);

  const addIngredient = (ing: Ingredient) => {
    if (!recipe) return;
    if (recipe.ingredients.some(ri => ri.ingredientId === ing.id)) {
      toast.error('Duplicate', `${ing.name} is already in the recipe.`);
      return;
    }
    const ri = createRecipeIngredient(ing, 0);
    const newIngs = [...recipe.ingredients, ri];
    const { ingredients, totalCost } = recalcRecipe(newIngs, pantryMap);
    updateRecipe(r => ({ ...r, ingredients, totalCost }));
    setShowIngSearch(false);
    setSearchTerm('');
  };

  const removeIngredient = (ingredientId: string) => {
    if (!recipe) return;
    const newIngs = recipe.ingredients.filter(ri => ri.ingredientId !== ingredientId);
    const { ingredients, totalCost } = recalcRecipe(newIngs, pantryMap);
    updateRecipe(r => ({ ...r, ingredients, totalCost }));
  };

  const updateQty = (ingredientId: string, qty: number) => {
    if (!recipe) return;
    const newIngs = recipe.ingredients.map(ri =>
      ri.ingredientId === ingredientId ? { ...ri, usageQty: qty } : ri
    );
    const { ingredients, totalCost } = recalcRecipe(newIngs, pantryMap);
    updateRecipe(r => ({ ...r, ingredients, totalCost }));
  };

  const toggleAllergen = (a: Allergen) => {
    updateRecipe(r => ({
      ...r,
      allergens: r.allergens.includes(a)
        ? r.allergens.filter(x => x !== a)
        : [...r.allergens, a],
    }));
  };

  // ── Save ──
  const handleSave = async () => {
    if (!recipe) return;
    setSaving(true);
    try {
      await apiFetch(`/api/menu/recipes/${profileId}/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(recipe),
      });
      setDirty(false);
      onCostUpdate?.(recipe.totalCost);
      window.dispatchEvent(new CustomEvent(MENU_DATA_UPDATED_EVENT));
      toast.success('Recipe Saved', `Cost: ${recipe.totalCost.toFixed(2)} ${currencySymbol}`);
    } catch {
      toast.error('Save Failed', 'Could not save recipe.');
    }
    setSaving(false);
  };

  // ── Kitchen Card Preview (replaces old direct-download) ──
  const handleOpenCardPreview = () => setShowCardPreview(true);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={20} className="animate-spin text-indigo-500" />
      <span className="ml-2 text-sm text-slate-400">Loading recipe…</span>
    </div>
  );

  if (!recipe) return null;

  const costPct = foodCostPct(recipe.totalCost, basePrice);
  const margin = profitMargin(recipe.totalCost, basePrice);

  return (
    <div className="space-y-5">
      {/* ── Financial Summary (Admin only) ── */}
      {canViewCost && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-3 text-center">
            <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Total Cost</p>
            <p className="text-lg font-bold text-emerald-800">{recipe.totalCost.toFixed(2)}</p>
            <p className="text-[10px] text-emerald-500">{currencySymbol}</p>
          </div>
          <div className={cn('bg-gradient-to-br border rounded-xl p-3 text-center',
            costPct > 35 ? 'from-red-50 to-red-100 border-red-200' : 'from-blue-50 to-blue-100 border-blue-200')}>
            <p className={cn('text-[9px] font-bold uppercase tracking-wider', costPct > 35 ? 'text-red-600' : 'text-blue-600')}>Food Cost %</p>
            <p className={cn('text-lg font-bold', costPct > 35 ? 'text-red-800' : 'text-blue-800')}>{costPct.toFixed(1)}%</p>
            {costPct > 35 && <p className="text-[10px] text-red-500">⚠️ Above 35%</p>}
          </div>
          <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-xl p-3 text-center">
            <p className="text-[9px] font-bold text-violet-600 uppercase tracking-wider">Profit Margin</p>
            <p className="text-lg font-bold text-violet-800">{margin.toFixed(2)}</p>
            <p className="text-[10px] text-violet-500">QAR</p>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-600">
          <Eye size={14} className="text-slate-500 shrink-0" />
          <p>Read-only access. Non-admin users can review recipe and prep data here, but only admins can change ingredients or financial values.</p>
        </div>
      )}

      {/* ── Ingredients Section ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
            <Scale size={13} className="text-teal-500" /> Ingredients
            <span className="text-[10px] text-slate-400 font-normal ml-1">({recipe.ingredients.length})</span>
          </p>
          <button onClick={() => { if (!canEdit) return; setShowIngSearch(true); setTimeout(() => searchRef.current?.focus(), 100); }}
            disabled={!canEdit}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg text-[10px] font-bold hover:bg-teal-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus size={11} /> Add from Pantry
          </button>
        </div>

        {/* Ingredient Search Dropdown */}
        {showIngSearch && (
          <div className="bg-white border border-indigo-200 rounded-xl shadow-lg p-3 space-y-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input ref={searchRef} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search pantry ingredients…"
                className="w-full pl-8 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-400" />
              <button onClick={() => { setShowIngSearch(false); setSearchTerm(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded">
                <X size={12} className="text-slate-400" />
              </button>
            </div>
            <div className="max-h-40 overflow-auto space-y-0.5">
              {searchResults.length === 0 ? (
                <p className="text-[10px] text-slate-400 py-2 text-center">No ingredients found. Add them in the Ingredient Registry first.</p>
              ) : searchResults.map(ing => (
                <button key={ing.id} onClick={() => addIngredient(ing)}
                  disabled={!canEdit || recipe.ingredients.some(ri => ri.ingredientId === ing.id)}
                  className={cn('w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-xs transition-colors',
                    (!canEdit || recipe.ingredients.some(ri => ri.ingredientId === ing.id))
                      ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                      : 'hover:bg-teal-50 text-slate-700')}>
                  <span className="font-semibold">{ing.name}</span>
                  <span className="text-[10px] text-slate-400">
                    {canViewCost && <>{ing.purchasePrice.toFixed(2)} {currencySymbol} / </>}{ing.purchaseUnit}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ingredient List */}
        {recipe.ingredients.length === 0 ? (
          <div className="bg-slate-50 rounded-xl p-6 text-center border border-dashed border-slate-200">
            <ChefHat size={24} className="mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-400">No ingredients added yet. Click "Add from Pantry" above.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-[9px] font-bold text-slate-500 uppercase">Ingredient</th>
                  <th className="px-3 py-2 text-[9px] font-bold text-slate-500 uppercase text-center" style={{ width: 100 }}>Usage Qty</th>
                  <th className="px-3 py-2 text-[9px] font-bold text-slate-500 uppercase text-center" style={{ width: 50 }}>Unit</th>
                  {canViewCost && <th className="px-3 py-2 text-[9px] font-bold text-slate-500 uppercase text-right" style={{ width: 80 }}>Cost</th>}
                  <th className="px-3 py-2" style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recipe.ingredients.map(ri => (
                  <tr key={ri.ingredientId} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2">
                      <span className="text-xs font-semibold text-slate-700">{ri.ingredientName}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="number" step="0.001" min="0"
                        value={ri.usageQty || ''}
                        onChange={e => updateQty(ri.ingredientId, Number(e.target.value) || 0)}
                        disabled={!canEdit}
                        className="w-20 text-center text-xs font-mono border border-slate-200 rounded-lg py-1.5 focus:outline-none focus:border-indigo-400 bg-white disabled:opacity-60 disabled:bg-slate-50" />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] text-slate-400 font-mono">{ri.usageUnit}</span>
                    </td>
                    {canViewCost && (
                      <td className="px-3 py-2 text-right">
                        <span className="text-xs font-bold text-slate-700">{ri.lineCost.toFixed(3)}</span>
                      </td>
                    )}
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => removeIngredient(ri.ingredientId)} disabled={!canEdit}
                        className="p-1 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40 disabled:hover:bg-transparent">
                        <Trash2 size={12} className="text-red-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {canViewCost && (
                <tfoot>
                  <tr className="bg-slate-50 border-t border-slate-200">
                    <td colSpan={3} className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase">Total Unit Cost</td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-emerald-700">{recipe.totalCost.toFixed(2)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ── Preparation Steps ── */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
          <ClipboardList size={13} className="text-indigo-500" /> Preparation Steps
        </p>
        <textarea
          value={recipe.prepSteps}
          onChange={e => updateRecipe(r => ({ ...r, prepSteps: e.target.value }))}
          disabled={!canEdit}
          placeholder="Step 1: Season the chicken with salt and pepper.&#10;Step 2: Heat oil in a pan over medium heat.&#10;Step 3: Cook for 5 minutes per side.&#10;&#10;(One step per line)"
          rows={5}
          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs leading-relaxed focus:outline-none focus:border-indigo-400 resize-none disabled:opacity-60 disabled:bg-slate-50" />
        <p className="text-[9px] text-slate-400">One step per line. These appear on the Kitchen Recipe Card.</p>
      </div>

      {/* ── Portion Control & Prep Time ── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Standard Yield (portions)</label>
          <input type="number" min="1" step="1"
            value={recipe.standardYield}
            onChange={e => updateRecipe(r => ({ ...r, standardYield: Math.max(1, Number(e.target.value) || 1) }))}
            disabled={!canEdit}
            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 disabled:opacity-60 disabled:bg-slate-50" />
        </div>
        <div>
          <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Prep Time (minutes)</label>
          <input type="number" min="0" step="1"
            value={recipe.prepTime ?? ''}
            onChange={e => updateRecipe(r => ({ ...r, prepTime: Number(e.target.value) || undefined }))}
            disabled={!canEdit}
            placeholder="e.g. 15"
            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 disabled:opacity-60 disabled:bg-slate-50" />
        </div>
      </div>

      {/* ── Allergens ── */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
          <ShieldAlert size={13} className="text-amber-500" /> Allergens
        </p>
        <div className="flex flex-wrap gap-2">
          {ALLERGEN_LIST.map(a => {
            const active = recipe.allergens.includes(a);
            return (
              <button key={a} type="button" onClick={() => toggleAllergen(a)} disabled={!canEdit}
                className={cn('px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-colors',
                  active
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300',
                  !canEdit && 'opacity-60 cursor-not-allowed hover:border-slate-200')}>
                {active && <Check size={10} className="inline -mt-0.5 mr-1" />}{a}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
        <button onClick={handleOpenCardPreview}
          disabled={recipe.ingredients.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 disabled:opacity-40 transition-colors">
          <Eye size={13} /> 🍳 Kitchen Card
        </button>
        <div className="flex-1" />
        {canEdit && dirty && <span className="text-[10px] text-amber-500 font-bold">Unsaved changes</span>}
        {canEdit && (
          <button onClick={handleSave} disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50 shadow-sm transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Save Recipe
          </button>
        )}
      </div>

      {/* ── Kitchen Card Preview Panel ── */}
      {showCardPreview && recipe && (
        <KitchenCardPreview
          open={showCardPreview}
          onClose={() => setShowCardPreview(false)}
          recipe={recipe}
          itemName={itemName}
          itemImage={itemImage}
          profileId={profileId}
          brandName={brandName}
          brandLogo={brandLogo}
        />
      )}
    </div>
  );
}
